import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { btn } from "@/components/Page";
import Modal from "@/components/Modal";
import ConfirmDialog from "@/components/ConfirmDialog";

export const RANK_META = {
  dominus: { label: "Dominus", cls: "text-amber-300 border-amber-600/50 bg-amber-950/30" },
  magister: { label: "Magister", cls: "text-sky-300 border-sky-600/50 bg-sky-950/30" },
  veteran: { label: "Veteran", cls: "text-emerald-300 border-emerald-700/50 bg-emerald-950/20" },
  lanista: { label: "Lanista", cls: "text-zinc-400 border-zinc-700 bg-zinc-900/40" },
};

export function RankBadge({ rank, perms }) {
  const meta = RANK_META[rank] || RANK_META.lanista;
  if (rank === "lanista") return null;
  const tags = [];
  if (rank === "magister") {
    if (perms?.spend_points) tags.push("Pts");
    if (perms?.treasury) tags.push("Treasury");
    if (perms?.announce) tags.push("News");
    if (perms?.armory) tags.push("Armory");
  }
  return (
    <span data-testid={`rank-badge-${rank}`} className={`text-[9px] uppercase tracking-wider font-cinzel px-1.5 py-0.5 border rounded-sm ${meta.cls}`}>
      {meta.label}{tags.length > 0 && <span className="opacity-70"> · {tags.join("/")}</span>}
    </span>
  );
}

export function RankManager({ member, a, onClose, reload }) {
  const [rank, setRank] = useState("lanista");
  const [spend, setSpend] = useState(false);
  const [treasury, setTreasury] = useState(false);
  const [announce, setAnnounce] = useState(false);
  const [armory, setArmory] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDominus, setConfirmDominus] = useState(false);

  useEffect(() => {
    if (member) {
      setRank(member.rank || "lanista");
      setSpend(!!member.perms?.spend_points);
      setTreasury(!!member.perms?.treasury);
      setAnnounce(!!member.perms?.announce);
      setArmory(!!member.perms?.armory);
    }
  }, [member]);

  if (!member) return null;
  const magCount = a.magister_count || 0;
  const isMag = member.rank === "magister";
  const magFull = !isMag && magCount >= (a.max_magisters || 3);

  const save = async (targetRank) => {
    setBusy(true);
    try {
      const body = { user_id: member.user_id, rank: targetRank };
      if (targetRank === "magister") { body.spend_points = spend; body.treasury = treasury; body.announce = announce; body.armory = armory; }
      await api.post("/alliance/rank", body);
      toast.success(targetRank === "dominus" ? `${member.name} is now Dominus` : `${member.name} is now ${RANK_META[targetRank].label}`);
      setConfirmDominus(false);
      onClose(); reload();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open={!!member} onClose={onClose} title={`Manage ${member.name}`}>
      <p className="text-xs text-zinc-500 mb-4">Set this member's rank. Magisters are officers (max {a.max_magisters || 3}) — grant them specific powers below.</p>
      <div className="space-y-2 mb-4">
        {["lanista", "veteran", "magister"].map((r) => (
          <button key={r} data-testid={`set-rank-${r}`} onClick={() => setRank(r)}
            disabled={r === "magister" && magFull && rank !== "magister"}
            className={`w-full text-left px-3 py-2.5 border rounded-sm transition-colors ${rank === r ? RANK_META[r].cls : "border-zinc-800 text-zinc-400 hover:border-zinc-600"} ${r === "magister" && magFull && rank !== "magister" ? "opacity-40 cursor-not-allowed" : ""}`}>
            <span className="font-cinzel uppercase tracking-wide text-sm">{RANK_META[r].label}</span>
            <span className="block text-[11px] text-zinc-500 normal-case tracking-normal">
              {r === "lanista" ? "Standard member." : r === "veteran" ? "Honorary title — no extra powers." : magFull && rank !== "magister" ? `Max ${a.max_magisters || 3} Magisters reached.` : "Co-leader with the powers you grant below."}
            </span>
          </button>
        ))}
      </div>

      {rank === "magister" && (
        <div className="border border-sky-900/40 bg-sky-950/10 p-3 mb-4 space-y-2" data-testid="magister-perms">
          <p className="text-[11px] uppercase tracking-widest text-sky-300/90 mb-1">Magister powers</p>
          <p className="text-[11px] text-zinc-500 -mt-1 mb-2">Magisters can always invite members & handle join requests.</p>
          <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
            <input type="checkbox" data-testid="perm-spend" checked={spend} onChange={(e) => setSpend(e.target.checked)} className="accent-sky-500" />
            Spend alliance skill points
          </label>
          <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
            <input type="checkbox" data-testid="perm-treasury" checked={treasury} onChange={(e) => setTreasury(e.target.checked)} className="accent-sky-500" />
            Access the treasury (send gold/spend gold on upgrades)
          </label>
          <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
            <input type="checkbox" data-testid="perm-announce" checked={announce} onChange={(e) => setAnnounce(e.target.checked)} className="accent-sky-500" />
            Post alliance announcements
          </label>
          <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
            <input type="checkbox" data-testid="perm-armory" checked={armory} onChange={(e) => setArmory(e.target.checked)} className="accent-sky-500" />
            Manage alliance armory (purchase, assign &amp; forge gear)
          </label>
        </div>
      )}

      <button data-testid="rank-save-btn" disabled={busy} onClick={() => save(rank)} className={btn("primary") + " w-full mb-2"}>Save rank</button>
      <button data-testid="rank-dominus-btn" disabled={busy} onClick={() => setConfirmDominus(true)}
        className="w-full py-2.5 border border-amber-700/50 text-amber-300 hover:bg-amber-950/30 transition-colors rounded-sm text-sm font-cinzel uppercase tracking-wide flex items-center justify-center gap-1.5">
        <Crown size={15} /> Promote to Dominus (hand over leadership)
      </button>

      <ConfirmDialog
        state={confirmDominus ? {
          title: "Hand over leadership",
          message: `Hand over leadership of ${a.name} to ${member.name}?\n\nThey will become the new DOMINUS and YOU will be demoted to Magister. This cannot be undone by you — only the new Dominus can give it back.`,
          confirmLabel: "Hand over",
          danger: true,
          onConfirm: () => save("dominus"),
        } : null}
        busy={busy}
        onClose={() => setConfirmDominus(false)}
      />
    </Modal>
  );
}
